import { VerticalEntity } from 'store/verticals/types'
import { Period } from 'utils/types'
import { byYearValueIsExist } from 'containers/VerticalPage/common/utils'
import { getYearFromPeriod, numberIsBetween } from 'containers/VerticalPage/common/utils'

interface RevenueSum {
  [key: string]: number
}

interface YearData {
  exact: { [key: number]: string }
  extrapolated: { [key: number]: string }
  reportedByYear: { [key: number]: string }
  total: { [key: number]: string }
  documented: { [key: number]: string }
  totalWithoutVat?: { [key: number]: string }
  unReportedByYear?: { [key: number]: string }
  totalTaxesPaidByCDTFA?: { [key: number]: string }
  totalTaxesPaidByIvix?: { [key: number]: string }
  [key: string]: { [key: number]: string } | undefined
}

export const getVerticalRevenueBySourcesFromEntity = (entityData: VerticalEntity | null, period: Period) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)

  const sources = entityData.revenueSources.map(source => ({
    name: source.platform,
    value: (() => {
      let totalSum = 0
      const totalData = source.revenueByYear?.total

      for (const key in totalData) {
        if (
          Object.prototype.hasOwnProperty.call(totalData, key) &&
          numberIsBetween(parseInt(key), startYear, endYear)
        ) {
          totalSum += totalData[key]
        }
      }

      return totalSum
    })(),
  }))

  let revenueSum: RevenueSum = {}

  sources.forEach(source => {
    revenueSum = {
      ...revenueSum,
      [source.name]: source.value + (revenueSum[source.name] || 0),
    }
  })

  const sourcesSum = Object.keys(revenueSum).map(key => ({
    name: key,
    value: revenueSum[key],
  }))

  return sourcesSum
}

export const getVerticalRevenueByTotalFromEntity = (entityData: VerticalEntity | null, period: Period) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)

  let exactSum = 0
  let extrapolatedSum = 0

  entityData.revenueSources.forEach(source => {
    const exactData = source.revenueByYear?.exact
    const extrapolatedData = source.revenueByYear?.extrapolated

    for (const key in exactData) {
      if (
        Object.prototype.hasOwnProperty.call(exactData, key) &&
        numberIsBetween(parseFloat(key), startYear, endYear)
      ) {
        exactSum += exactData[key]
      }
    }

    for (const key in extrapolatedData) {
      if (
        Object.prototype.hasOwnProperty.call(extrapolatedData, key) &&
        numberIsBetween(parseFloat(key), startYear, endYear)
      ) {
        extrapolatedSum += extrapolatedData[key]
      }
    }
  })

  if (extrapolatedSum === 0) return [{ name: 'Documented', value: exactSum }]

  return [
    { name: 'Documented', value: exactSum },
    { name: 'Estimated', value: extrapolatedSum },
  ]
}

export const getTotalRevenueFromEntity = (entityData: VerticalEntity | null, period: Period) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)
  const data = entityData.totals.byYear as unknown as YearData
  const years = new Set<string>()

  const withoutVatIsExist = byYearValueIsExist(data as { [key: string]: { [key: number]: string } }, 'totalWithoutVat')

  for (const category in data) {
    const categoryData = data[category]
    if (categoryData) {
      Object.keys(categoryData).forEach(year => years.add(year))
    }
  }

  const result = Array.from(years)
    .sort()
    .map(year => {
      const yearDataObject: {
        date: string
        exact: number
        extrapolated: number
        documented: number
        estimated: number
        total: number
        reported: number
        unreported: number
        withoutVat?: number
      } = {
        date: year,
        exact: Number(data?.exact?.[Number(year)] || 0),
        extrapolated: Number(data?.extrapolated?.[Number(year)] || 0),
        documented: Number(data?.exact?.[Number(year)] || 0),
        estimated: Number(data?.extrapolated?.[Number(year)] || 0),
        total: Number(data?.total?.[Number(year)] || 0),
        reported: Number(data?.reportedByYear?.[Number(year)] || 0),
        unreported: Number(data?.unReportedByYear?.[Number(year)] || 0),
      }

      if (withoutVatIsExist) {
        yearDataObject.withoutVat = Number(data?.totalWithoutVat?.[Number(year)] || 0)
      }

      return yearDataObject
    })
    .filter(item => numberIsBetween(parseFloat(item.date), startYear, endYear))

  return result
}

export const getLabelsForFinancialOverview = (
  totalRevenueGraphData: {
    [key: string]: string | number
  }[],
) => {
  const values = [
    { key: 'total', value: 'Total Revenue' },
    { key: 'reported', value: 'Reported' },
    { key: 'unreported', value: 'Unreported' },
  ]

  if (totalRevenueGraphData.some(item => typeof item['withoutVat'] === 'number')) {
    values.push({ key: 'withoutVat', value: 'Revenue w/o vat' })
  }

  return values
}
